// src/components/health/dashboard/HealthDashboard.tsx
// Main Health Dashboard Layout Component

import React from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  FileText, 
  Download, 
  Clock,
  TrendingUp,
  Bell,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { HealthQuickStats } from './HealthQuickStats';
import { HealthAlerts } from './HealthAlerts';
import { RecentHealthActivity } from './RecentHealthActivity';
import { HealthInsights } from './HealthInsights';
import type { HealthDashboardData } from '@/features/health/types/healthTypes/reportTypes';

// ============================================================================
// Types
// ============================================================================

interface HealthDashboardProps {
  data: HealthDashboardData;
  childId: string;
  onAddEntry: () => void;
  onViewReports: () => void;
  onExport: () => void;
  onViewTimeline: () => void;
  isLoading?: boolean;
}

// ============================================================================
// Animation Variants
// ============================================================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export const HealthDashboard: React.FC<HealthDashboardProps> = ({
  data,
  childId,
  onAddEntry,
  onViewReports,
  onExport,
  onViewTimeline,
  isLoading = false,
}) => {
  if (isLoading) {
    return <HealthDashboardSkeleton />;
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header with Quick Actions */}
      <motion.div 
        variants={itemVariants}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-display font-semibold text-navy">
            Health Tracking
          </h1>
          <p className="text-warm-gray mt-1">
            {data.child.name}'s health overview
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={onViewTimeline}
            className="hidden sm:flex"
          >
            <Clock className="w-4 h-4 mr-2" />
            Timeline
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onExport}
            className="hidden sm:flex"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onViewReports}
          >
            <FileText className="w-4 h-4 mr-2" />
            Reports
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onAddEntry}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </motion.div>

      {/* Alerts Section */}
      {data.alerts.filter(a => !a.dismissed).length > 0 && (
        <motion.div variants={itemVariants}>
          <HealthAlerts alerts={data.alerts} />
        </motion.div>
      )}

      {/* Quick Stats */}
      <motion.div variants={itemVariants}>
        <HealthQuickStats stats={data.stats} childId={childId} />
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity - Takes 2 columns */}
        <motion.div 
          variants={itemVariants}
          className="lg:col-span-2"
        >
          <RecentHealthActivity 
            logs={data.recentLogs}
            medications={data.upcomingMedications}
            onViewAll={onViewTimeline}
          />
        </motion.div>

        {/* Insights Panel */}
        <motion.div variants={itemVariants}>
          <HealthInsights 
            insights={data.insights}
            onViewReports={onViewReports}
          />
        </motion.div>
      </div>

      {/* Quick Actions Grid */}
      <motion.div variants={itemVariants}>
        <h3 className="text-sm font-semibold text-navy mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {data.quickActions.map((action) => (
            <QuickActionButton
              key={action.id}
              label={action.label}
              icon={action.icon}
              count={action.count}
              onClick={() => {
                // Handle action navigation
                console.log('Quick action:', action.action);
              }}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================================================
// Quick Action Button
// ============================================================================

interface QuickActionButtonProps {
  label: string;
  icon: string;
  count?: number;
  onClick: () => void;
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({
  label,
  icon,
  count,
  onClick,
}) => {
  const IconComponent = getIconComponent(icon);

  return (
    <button
      onClick={onClick}
      className="
        flex items-center gap-3 p-4 bg-white rounded-xl border border-cream-dark
        hover:border-sage-light hover:shadow-soft transition-all duration-200
        group
      "
    >
      <div className="
        w-10 h-10 rounded-lg bg-cream flex items-center justify-center
        group-hover:bg-sage-light/20 transition-colors duration-200
      ">
        <IconComponent className="w-5 h-5 text-sage-dark" />
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-medium text-navy">{label}</p>
        {count !== undefined && (
          <p className="text-xs text-warm-gray">{count} items</p>
        )}
      </div>
    </button>
  );
};

// ============================================================================
// Icon Helper
// ============================================================================

function getIconComponent(iconName: string) {
  const icons: Record<string, React.FC<{ className?: string }>> = {
    'plus': Plus,
    'file-text': FileText,
    'download': Download,
    'clock': Clock,
    'trending-up': TrendingUp,
    'bell': Bell,
  };

  return icons[iconName] || Plus;
}

// ============================================================================
// Loading Skeleton
// ============================================================================

const HealthDashboardSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    {/* Header Skeleton */}
    <div className="flex items-center justify-between">
      <div>
        <div className="h-8 w-48 bg-cream-dark rounded" />
        <div className="h-4 w-32 bg-cream-dark rounded mt-2" />
      </div>
      <div className="flex gap-3">
        <div className="h-10 w-24 bg-cream-dark rounded-lg" />
        <div className="h-10 w-32 bg-cream-dark rounded-lg" />
      </div>
    </div>

    {/* Stats Skeleton */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-24 bg-cream-dark rounded-xl" />
      ))}
    </div>

    {/* Content Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 h-80 bg-cream-dark rounded-xl" />
      <div className="h-80 bg-cream-dark rounded-xl" />
    </div>
  </div>
);

export default HealthDashboard;

